import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../apiClient';

// Mirrors apps/api's TASK_TYPE_CATEGORY_VALUES -- apps/web can't import packages/shared (ADR 0019),
// and this is too small a list to be worth a network round trip just to populate a dropdown.
const TASK_TYPE_CATEGORIES = [
  { value: 'Billable', label: 'Billable' },
  { value: 'NonBillableProductive', label: 'Non-Billable Productive' },
  { value: 'NonBillable', label: 'Non-Billable' },
  // Non-billable like the value above it, but also out of My Time's utilization denominator -- that
  // behavior is why it's a category rather than a name rule (Jeremy, 2026-08-12).
  { value: 'TimeOff', label: 'Time Off' },
];

// Mirrors apps/api's ACTIVITY_BILLED_TO_VALUES -- same can't-import-shared reasoning as above.
const ACTIVITY_BILLED_TO = [
  { value: 'Project', label: 'Project' },
  { value: 'Market', label: 'Market' },
  { value: 'CoreProduct', label: 'Core Product' },
  { value: 'Administrative', label: 'Administrative' },
];

// Mirrors apps/api's COMMENT_POLICY_VALUES -- same can't-import-shared reasoning as above.
const COMMENT_POLICIES = [
  { value: 'NotAvailable', label: 'Not available' },
  { value: 'Available', label: 'Available' },
  { value: 'Required', label: 'Required' },
];

// Mirrors apps/api's USER_ROLE_VALUES -- same can't-import-shared reasoning as above.
const USER_ROLES = [
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'rnd', label: 'R&D (viewer + R&D time entry)' },
  { value: 'admin', label: 'Admin' },
];

// Mirrors apps/api's PROJECT_STATUS_VALUES / PROJECT_SETUP_STATUS_VALUES
// (project-status.constants.ts) -- same can't-import-shared reasoning as TASK_TYPE_CATEGORIES above.
export const PROJECT_STATUSES = ['Placeholder', 'Startup', 'Active', 'Final Acceptance', 'Converted', 'Completed', 'Archived'];
export const PROJECT_SETUP_STATUSES = ['Pending', 'In Progress', 'Complete'];

// The two stages Project Setup works through -- Step 1 lists only projects in one of them.
export const PROJECT_SETUP_ELIGIBLE_STATUSES = ['Placeholder', 'Startup'];

// Mirrors apps/api's PROJECT_TYPE_VALUES. How the engagement is contracted and recognized, as
// opposed to a stage it passes through: POC is percentage-of-completion (fixed fee), T&M is time
// and materials, Internal is non-billable, B&B (Book and Bill) is fixed-fee like POC.
export const PROJECT_TYPES = ['POC', 'POC (Milestone)', 'T&M', 'Internal', 'B&B'];

// Mirrors SCHEDULABLE_PROJECT_TYPES. T&M is billed against hours rather than against a plan, so
// there is nothing for the Project Schedule to promote -- its picker leaves those out.
export const SCHEDULABLE_PROJECT_TYPES = ['POC', 'POC (Milestone)', 'Internal', 'B&B'];

// Mirrors FIXED_FEE_PROJECT_TYPES -- "real billable delivery work" for the BPA/Registers pickers,
// portfolio dashboard, and customer preview. Narrower than SCHEDULABLE_PROJECT_TYPES: Internal is
// excluded here too.
export const FIXED_FEE_PROJECT_TYPES = ['POC', 'POC (Milestone)', 'B&B'];

export function ErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div
      style={{
        padding: '12px 16px', borderRadius: 8, marginBottom: 20,
        background: 'rgba(239,65,54,0.08)', border: '1px solid rgba(239,65,54,0.3)',
        fontSize: 13, color: 'var(--red)',
      }}
    >
      ⚠ {error}
    </div>
  );
}

// ---------- Sorting (shared by every grid on this page) ----------

// Every grid here sorts by any column (Jeremy, 2026-08-12), so the header and the compare live once
// rather than per tab. Sort key '' means "leave the server's order alone" -- that's each grid's
// starting state, so opening a tab looks exactly as it did before anyone clicks.
export function SortTh({ label, sortKey, sort, onSort, style, title }) {
  const active = sort.key === sortKey;
  return (
    <th className="sortable" style={style} title={title} onClick={() => onSort(sortKey)}>
      {label}
      <span className={`sort-arrow${active ? '' : ' is-idle'}`}>{active && sort.dir === 'desc' ? '▼' : '▲'}</span>
    </th>
  );
}

export function useGridSort(initialKey = '') {
  const [sort, setSort] = useState({ key: initialKey, dir: 'asc' });
  const toggleSort = useCallback((key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }, []);
  return [sort, toggleSort];
}

// Capacity is stored as a 0-1 multiplier (capacity.service.ts multiplies by it) but maintained as a
// percentage on the People grid (Jeremy, 2026-09-16). The draft holds the percent as typed and the
// conversion happens once, on save -- converting on every keystroke would round-trip a half-typed
// "7" through 0.07 and back, fighting the person entering 75.
export function capacityPctOf(user) {
  return String(Math.round((user.capacityFactor ?? 1) * 100));
}

// undefined means this row's capacity wasn't edited, so the key is dropped from the PATCH body and
// the server keeps what it has -- a row dirtied by some other column must not re-scale a value
// nobody touched.
export function capacityFactorFromPct(draftValue) {
  if (draftValue === undefined || draftValue === '') return undefined;
  return Number(draftValue) / 100;
}

// `valueFor` reads each row's saved value, not an in-flight draft -- otherwise a row being edited
// could jump position under the cursor mid-keystroke.
export function sortRows(rows, sort, valueFor) {
  if (!sort.key) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  // Array.sort is stable, so rows that tie keep the server's relative order.
  return [...rows].sort((a, b) => {
    const av = valueFor(a, sort.key);
    const bv = valueFor(b, sort.key);
    if (av < bv) return -dir;
    if (av > bv) return dir;
    return 0;
  });
}

export const lower = (value) => (value ?? '').toString().toLowerCase();

/**
 * Row class for an inline-editable grid: a yellow left-edge bar while a row has an unsaved edit
 * pending (`.sheet tr.is-dirty` in App.css), alternating background otherwise. Shared by BPATracker's
 * grids and the Project Registers grids, so the two can't quietly diverge on the same convention.
 *
 * Banding is computed from the rendered index rather than left to CSS `:nth-child(even)`: an expanded
 * row's detail panel is a second `<tr>` immediately after it, so `nth-child` would count it and
 * expanding any row would invert the banding of every row below it. Sorting and filtering change the
 * visible order too, and the index is what the user actually sees.
 */
/**
 * A `.cell-area` textarea that grows to fit its content instead of scrolling internally -- so a
 * multi-line value (a CR description, a RAAIDD plan field) is always fully visible in the grid, not
 * just while focused (contrast `.cell-area-expand`, which only grows on focus). The row simply gets
 * taller, the same as a `whiteSpace: pre-wrap` read-only cell already does.
 */
export function AutoGrowTextarea({ value, className = 'cell-area', style, ...props }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      className={className}
      value={value}
      rows={1}
      style={{ overflow: 'hidden', resize: 'none', ...style }}
      {...props}
    />
  );
}

export function rowClassName(index, isDirty) {
  const classes = [];
  if (isDirty) classes.push('is-dirty');
  if (index % 2 === 1) classes.push('is-alt');
  return classes.length ? classes.join(' ') : undefined;
}

// Sorts a dropdown column by the label on screen, not the underlying enum value.
const labelOf = (options, value) => lower(options.find((o) => o.value === value)?.label);

// ---------- Teams ----------

export function TeamsTab() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState('');
  const [newTeam, setNewTeam] = useState({ name: '', standardMonthlyHours: '' });
  const [adding, setAdding] = useState(false);
  const [sort, toggleSort] = useGridSort();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // includeInactive=true -- unlike every other /teams caller, this admin tab needs to see (and
      // be able to reactivate) inactive teams, not just the active-only default list.
      setTeams(await apiFetch('/teams?includeInactive=true'));
      setDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function fieldValue(team, field) {
    const draft = drafts[team.id];
    if (draft && draft[field] !== undefined) return draft[field];
    return team[field] ?? '';
  }

  function setField(teamId, field, value) {
    setDrafts((prev) => ({ ...prev, [teamId]: { ...prev[teamId], [field]: value } }));
  }

  async function saveTeam(team) {
    setSavingId(team.id);
    setError('');
    try {
      await apiFetch(`/teams/${team.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: fieldValue(team, 'name'),
          standardMonthlyHours: fieldValue(team, 'standardMonthlyHours')
            ? Number(fieldValue(team, 'standardMonthlyHours'))
            : undefined,
          isActive: fieldValue(team, 'isActive'),
        }),
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId('');
    }
  }

  const sortedTeams = useMemo(
    () => sortRows(teams, sort, (team, key) => {
      switch (key) {
        case 'standardMonthlyHours': return team.standardMonthlyHours ?? 0;
        case 'isActive': return team.isActive ? 1 : 0;
        default: return lower(team.name);
      }
    }),
    [teams, sort],
  );

  async function addTeam(e) {
    e.preventDefault();
    if (!newTeam.name.trim()) return;
    setAdding(true);
    setError('');
    try {
      await apiFetch('/teams', {
        method: 'POST',
        body: JSON.stringify({
          name: newTeam.name.trim(),
          standardMonthlyHours: newTeam.standardMonthlyHours ? Number(newTeam.standardMonthlyHours) : undefined,
        }),
      });
      setNewTeam({ name: '', standardMonthlyHours: '' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  return (
    <div>
      <ErrorBanner error={error} />
      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <div className="sheet-wrap is-fit" style={{ marginBottom: 16 }}>
            <table className="sheet">
              <thead>
                <tr>
                  <SortTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} style={{ minWidth: 200 }} />
                  <SortTh
                    label="Standard monthly hours" sortKey="standardMonthlyHours" sort={sort} onSort={toggleSort}
                    style={{ textAlign: 'center', width: 180 }}
                  />
                  <SortTh label="Active" sortKey="isActive" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 70 }} />
                  <th style={{ width: 100 }} />
                </tr>
              </thead>
              <tbody>
                {sortedTeams.map((team) => {
                  const dirty = !!drafts[team.id];
                  return (
                    <tr key={team.id} className={dirty ? 'is-dirty' : ''} style={{ opacity: fieldValue(team, 'isActive') ? 1 : 0.55 }}>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(team, 'name')}
                          onChange={(e) => setField(team.id, 'name', e.target.value)}
                        />
                      </td>
                      <td className="cell-num">
                        <input
                          type="number"
                          min="0"
                          style={{ width: 100, textAlign: 'center', border: 'none', background: 'transparent', font: 'inherit' }}
                          value={fieldValue(team, 'standardMonthlyHours')}
                          onChange={(e) => setField(team.id, 'standardMonthlyHours', e.target.value)}
                        />
                      </td>
                      <td className="cell-check" style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={fieldValue(team, 'isActive')}
                          onChange={(e) => setField(team.id, 'isActive', e.target.checked)}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {dirty && (
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: 11, padding: '4px 10px' }}
                            disabled={savingId === team.id}
                            onClick={() => saveTeam(team)}
                          >
                            {savingId === team.id ? 'Saving…' : 'Save'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {teams.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                      No teams yet — add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="card-title">Add a team</div>
            <form onSubmit={addTeam} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, width: 220 }}>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. IE"
                  maxLength={100}
                  value={newTeam.name}
                  onChange={(e) => setNewTeam((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 180 }}>
                <label className="form-label">Standard monthly hours</label>
                <input
                  type="number"
                  min="0"
                  className="form-input"
                  placeholder="e.g. 150"
                  value={newTeam.standardMonthlyHours}
                  onChange={(e) => setNewTeam((f) => ({ ...f, standardMonthlyHours: e.target.value }))}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={adding || !newTeam.name.trim()}>
                {adding ? 'Adding…' : '+ Add'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Task Types ----------

export function TaskTypesTab() {
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [newType, setNewType] = useState({ name: '', code: '' });
  const [adding, setAdding] = useState(false);
  const [sort, toggleSort] = useGridSort();
  // Off by default so the tab still opens as the list people can actually log against. On, it
  // surfaces the import-created types -- OnePlan task names that matched nothing on import, which
  // are exactly the rows someone comes here to reactivate or rename (Jeremy, 2026-08-13).
  const [showInactive, setShowInactive] = useState(false);

  // This UI directly maintains the list (Jeremy, 2026-08-05) -- both starting sets load
  // automatically instead of behind a button. Both are create-if-missing only (TaskTypesService),
  // so this never reverts a name/code/category edit made here on a later page load.
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        apiFetch('/task-types/seed', { method: 'POST' }),
        apiFetch('/task-types/seed-administrative', { method: 'POST' }),
      ]);
      setTaskTypes(await apiFetch(`/task-types${showInactive ? '?includeInactive=true' : ''}`));
      setDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  function fieldValue(type, field) {
    const draft = drafts[type.id];
    if (draft && draft[field] !== undefined) return draft[field];
    return type[field] ?? '';
  }

  function setField(id, field, value) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }

  const sortedTypes = useMemo(
    () => sortRows(taskTypes, sort, (type, key) => {
      switch (key) {
        case 'category': return labelOf(TASK_TYPE_CATEGORIES, type.category);
        case 'activityBilledTo': return labelOf(ACTIVITY_BILLED_TO, type.activityBilledTo);
        case 'commentPolicy': return labelOf(COMMENT_POLICIES, type.commentPolicy);
        case 'requiresAccount': return type.requiresAccount ? 1 : 0;
        case 'isActive': return type.isActive ? 1 : 0;
        default: return lower(type[key]);
      }
    }),
    [taskTypes, sort],
  );

  function patchType(type) {
    return apiFetch(`/task-types/${type.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: fieldValue(type, 'name'),
        code: fieldValue(type, 'code') || undefined,
        category: fieldValue(type, 'category') || undefined,
        activityBilledTo: fieldValue(type, 'activityBilledTo') || undefined,
        requiresAccount: fieldValue(type, 'requiresAccount'),
        commentPolicy: fieldValue(type, 'commentPolicy') || undefined,
        isActive: fieldValue(type, 'isActive'),
      }),
    });
  }

  // One save for the whole grid (same convention as the People tab) instead of a button per row --
  // lets a mass edit pass happen in a single session.
  async function saveAll() {
    const dirtyIds = Object.keys(drafts);
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    setError('');
    const failedIds = new Set();
    const failureMessages = [];
    await Promise.all(
      dirtyIds.map(async (id) => {
        const type = taskTypes.find((t) => t.id === id);
        if (!type) return;
        try {
          await patchType(type);
        } catch (err) {
          failedIds.add(id);
          failureMessages.push(`${type.name}: ${err.message}`);
        }
      }),
    );
    try {
      setTaskTypes(await apiFetch('/task-types'));
    } catch (err) {
      failureMessages.push(err.message);
    }
    setDrafts((prev) => {
      const next = { ...prev };
      for (const id of dirtyIds) if (!failedIds.has(id)) delete next[id];
      return next;
    });
    setError(failureMessages.length > 0 ? `${failureMessages.length} update(s) failed: ${failureMessages.join('; ')}` : '');
    setSavingAll(false);
  }

  // Retiring a type is the Active checkbox, not a delete: TimeEntry.taskTypeId is NoAction
  // specifically so logged hours can't be orphaned, so a row is never removed from the database.
  // Inactive drops it out of GET /task-types' default list and every new-entry picker, while
  // "Show inactive" above brings it back here to be reactivated -- which is why the old one-way
  // × button is gone (Jeremy, 2026-08-13).

  async function addType(e) {
    e.preventDefault();
    if (!newType.name.trim()) return;
    setAdding(true);
    setError('');
    try {
      await apiFetch('/task-types', {
        method: 'POST',
        body: JSON.stringify({ name: newType.name.trim(), code: newType.code.trim() || undefined }),
      });
      setNewType({ name: '', code: '' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  const dirtyCount = Object.keys(drafts).length;

  return (
    <div>
      {dirtyCount > 0 && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
          </div>
          <button className="btn btn-primary" disabled={savingAll} onClick={saveAll}>
            {savingAll ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
      <ErrorBanner error={error} />

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 13, color: 'var(--text-mid)' }}>
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
        Show inactive task types
      </label>

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <div className="sheet-wrap is-fit" style={{ marginBottom: 16 }}>
            <table className="sheet">
              <thead>
                <tr>
                  <SortTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} style={{ width: 220 }} />
                  <SortTh label="Code" sortKey="code" sort={sort} onSort={toggleSort} style={{ width: 90 }} />
                  <SortTh label="Category" sortKey="category" sort={sort} onSort={toggleSort} style={{ width: 200 }} />
                  <SortTh
                    label="Activity Billed To" sortKey="activityBilledTo" sort={sort} onSort={toggleSort}
                    style={{ width: 150 }}
                    title="Which bucket this activity is billed to for reporting -- Project, Market, or Core Product"
                  />
                  <SortTh
                    label="Customer" sortKey="requiresAccount" sort={sort} onSort={toggleSort}
                    style={{ textAlign: 'center', width: 110 }}
                    title="Administrative time logged for a specific customer rather than a project -- e.g. Travel, Post Go Live Support. Never offered against a real project."
                  />
                  <SortTh
                    label="Comments" sortKey="commentPolicy" sort={sort} onSort={toggleSort}
                    style={{ width: 150 }}
                    title="Whether a timesheet row for this task can carry a comment for the week saying what the time was spent on"
                  />
                  <SortTh
                    label="Active" sortKey="isActive" sort={sort} onSort={toggleSort}
                    style={{ textAlign: 'center', width: 70 }}
                    title="Inactive types stay on hours already logged against them, but are never offered for new entry"
                  />
                </tr>
              </thead>
              <tbody>
                {sortedTypes.map((type) => {
                  const dirty = !!drafts[type.id];
                  return (
                    <tr key={type.id} className={dirty ? 'is-dirty' : ''}>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(type, 'name')}
                          onChange={(e) => setField(type.id, 'name', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(type, 'code')}
                          onChange={(e) => setField(type.id, 'code', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <select
                          className="form-select"
                          style={{ border: 'none', width: '100%' }}
                          value={fieldValue(type, 'category') || 'Billable'}
                          onChange={(e) => setField(type.id, 'category', e.target.value)}
                        >
                          {TASK_TYPE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '4px' }}>
                        <select
                          className="form-select"
                          style={{ border: 'none', width: '100%' }}
                          value={fieldValue(type, 'activityBilledTo') || 'Project'}
                          onChange={(e) => setField(type.id, 'activityBilledTo', e.target.value)}
                        >
                          {ACTIVITY_BILLED_TO.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </select>
                      </td>
                      <td className="cell-check" style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!fieldValue(type, 'requiresAccount')}
                          onChange={(e) => setField(type.id, 'requiresAccount', e.target.checked)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <select
                          className="form-select"
                          style={{ border: 'none', width: '100%' }}
                          value={fieldValue(type, 'commentPolicy') || 'NotAvailable'}
                          onChange={(e) => setField(type.id, 'commentPolicy', e.target.value)}
                        >
                          {COMMENT_POLICIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </td>
                      <td className="cell-check" style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!fieldValue(type, 'isActive')}
                          onChange={(e) => setField(type.id, 'isActive', e.target.checked)}
                        />
                      </td>
                    </tr>
                  );
                })}
                {taskTypes.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                      No task types yet — add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="card-title">Add a task type</div>
            <form onSubmit={addType} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, width: 220 }}>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  maxLength={100}
                  value={newType.name}
                  onChange={(e) => setNewType((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 100 }}>
                <label className="form-label">Code (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  maxLength={20}
                  value={newType.code}
                  onChange={(e) => setNewType((f) => ({ ...f, code: e.target.value }))}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={adding || !newType.name.trim()}>
                {adding ? 'Adding…' : '+ Add'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- People ----------

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// The starting point for a ramp, editable per month once generated (ADR 0043). A default rather than
// stored policy: a ramp that varies by role is a judgment call, and putting it in the schema would
// make every exception a migration. Ramping DOWN is the same control with different numbers —
// someone normally non-billable who was partly billable for a few months is "50, 50, 25", and the
// months that follow fall back to their base capacity on their own.
const DEFAULT_RAMP_PCTS = '40, 70, 100';

// Per-month capacity for one person. Sparse — no rows is the normal case, meaning "use their usual
// capacity every month". Percentages here, multipliers on the wire, same as the grid's Capacity cell.
function MonthlyCapacityModal({ user, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const today = new Date();
  const [draft, setDraft] = useState({
    year: String(today.getFullYear()),
    month: String(today.getMonth() + 1),
    capacityFactor: '',
    fteFraction: '',
    note: '',
  });
  const [rampStart, setRampStart] = useState('');
  const [rampPcts, setRampPcts] = useState(DEFAULT_RAMP_PCTS);
  const [rampNote, setRampNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await apiFetch(`/users/${user.id}/monthly-capacity`));
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  async function run(fn) {
    setSaving(true);
    setError('');
    try {
      await fn();
      await load();
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function addRow() {
    const pct = (value) => (value === '' ? null : Number(value) / 100);
    return run(() => apiFetch(`/users/${user.id}/monthly-capacity`, {
      method: 'PUT',
      body: JSON.stringify({
        year: Number(draft.year),
        month: Number(draft.month),
        capacityFactor: pct(draft.capacityFactor),
        fteFraction: pct(draft.fteFraction),
        note: draft.note || null,
      }),
    }));
  }

  function generateRamp() {
    const [year, month] = rampStart.split('-');
    const pcts = rampPcts.split(',').map((p) => Number(p.trim())).filter((p) => !Number.isNaN(p));
    return run(() => apiFetch(`/users/${user.id}/monthly-capacity/ramp`, {
      method: 'POST',
      body: JSON.stringify({ startYear: Number(year), startMonth: Number(month), pcts, note: rampNote || undefined }),
    }));
  }

  const asPct = (value) => (value == null ? '—' : `${Math.round(value * 100)}%`);

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{user.name} — capacity by month</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <ErrorBanner error={error} />
          <p style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 0 }}>
            Only months that differ from {user.name.split(' ')[0]}'s usual capacity need a row. Every
            other month uses the Capacity % on the People grid. Months outside the Start/End dates are
            ignored — the employment window wins.
          </p>

          <div className="modal-section">
            <div className="modal-section-title">Set a run of months</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">First month</label>
                <input type="month" className="form-input" value={rampStart} onChange={(e) => setRampStart(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 140 }}>
                <label className="form-label">Percentages</label>
                <input className="form-input" value={rampPcts} onChange={(e) => setRampPcts(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 130 }}>
                <label className="form-label">Note</label>
                <input className="form-input" placeholder="e.g. Onboarding ramp" value={rampNote} onChange={(e) => setRampNote(e.target.value)} />
              </div>
              <button className="btn btn-outline" disabled={!rampStart || saving} onClick={generateRamp}>Generate</button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>
              One row per percentage, consecutive from that month. Works in either direction —{' '}
              <code>40, 70, 100</code> ramps a new hire up, <code>50, 50, 25</code> covers someone
              temporarily more billable than usual. Months after the last one fall back to their
              Capacity % on the People grid automatically. Edit or delete any row below afterwards.
            </div>
          </div>

          <div className="modal-section">
            <div className="modal-section-title">Months</div>
            {loading ? <div>Loading…</div> : rows.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-light)' }}>No overrides — usual capacity every month.</div>
            ) : (
              <table className="sheet" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Month</th>
                    <th style={{ textAlign: 'right' }}>Capacity</th>
                    <th style={{ textAlign: 'right' }}>FTE</th>
                    <th style={{ textAlign: 'left' }}>Note</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.year}-${row.month}`}>
                      <td style={{ padding: '4px 8px' }}>{MONTH_NAMES[row.month - 1]} {row.year}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{asPct(row.capacityFactor)}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{asPct(row.fteFraction)}</td>
                      <td style={{ padding: '4px 8px', color: 'var(--text-mid)' }}>{row.note || ''}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                        <button
                          type="button"
                          className="btn btn-outline"
                          style={{ fontSize: 11, padding: '1px 6px' }}
                          disabled={saving}
                          onClick={() => run(() => apiFetch(`/users/${user.id}/monthly-capacity/${row.year}/${row.month}`, { method: 'DELETE' }))}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="modal-section">
            <div className="modal-section-title">Add or change one month</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, width: 110 }}>
                <label className="form-label">Month</label>
                <select className="form-select" value={draft.month} onChange={(e) => setDraft((d) => ({ ...d, month: e.target.value }))}>
                  {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 90 }}>
                <label className="form-label">Year</label>
                <input type="number" className="form-input" value={draft.year} onChange={(e) => setDraft((d) => ({ ...d, year: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 100 }}>
                <label className="form-label">Capacity %</label>
                <input type="number" min="0" max="100" className="form-input" value={draft.capacityFactor} onChange={(e) => setDraft((d) => ({ ...d, capacityFactor: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 90 }}>
                <label className="form-label">FTE %</label>
                <input type="number" min="0" max="100" className="form-input" value={draft.fteFraction} onChange={(e) => setDraft((d) => ({ ...d, fteFraction: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 120 }}>
                <label className="form-label">Note</label>
                <input className="form-input" value={draft.note} onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))} />
              </div>
              <button
                className="btn btn-primary"
                disabled={saving || (draft.capacityFactor === '' && draft.fteFraction === '')}
                onClick={addRow}
              >
                Save month
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>
              Leave FTE % blank for a billable-capacity change — someone ramping up or down is still
              the same FTE. Set it only for a genuine part-time change. Capacity % overrides whatever
              is on the People grid, including 0, so a normally non-billable person can be given a
              billable month.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PeopleTab() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [sort, toggleSort] = useGridSort('name');
  // The person whose per-month capacity is being edited, or null for closed.
  const [rampFor, setRampFor] = useState(null);

  // Override count per person, straight off /users' _count — labels the Ramp button without a
  // request per row.
  const rampCounts = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u._count?.monthlyCapacity ?? 0])),
    [users],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // includeViewers: this is the one screen that manages 'viewer' (EVP/Finance read-only)
      // accounts -- every other /users caller defaults to excluding them.
      const [userData, teamData] = await Promise.all([apiFetch('/users?includeViewers=true'), apiFetch('/teams')]);
      setUsers(userData);
      setTeams(teamData);
      setDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function fieldValue(user, field, defaultValue = '') {
    const draft = drafts[user.id];
    if (draft && draft[field] !== undefined) return draft[field];
    return user[field] ?? defaultValue;
  }

  function setField(userId, field, value) {
    setDrafts((prev) => ({ ...prev, [userId]: { ...prev[userId], [field]: value } }));
  }

  function capacityPct(user) {
    const draft = drafts[user.id];
    return draft && draft.capacityFactor !== undefined ? draft.capacityFactor : capacityPctOf(user);
  }

  // Date cells bind to <input type="date">, which needs a bare yyyy-mm-dd -- the API returns a full
  // ISO timestamp, so trim rather than round-tripping through Date (which would shift the day west
  // of Greenwich, exactly what ADR 0009 exists to prevent).
  function dateValue(user, field) {
    const draft = drafts[user.id];
    const raw = draft && draft[field] !== undefined ? draft[field] : user[field];
    return raw ? String(raw).slice(0, 10) : '';
  }

  const teamNameById = useMemo(() => new Map(teams.map((t) => [t.id, t.name])), [teams]);
  const userNameById = useMemo(() => new Map(users.map((u) => [u.id, u.name])), [users]);

  const sortedUsers = useMemo(
    () => sortRows(users, sort, (user, key) => {
      switch (key) {
        case 'team': return lower(teamNameById.get(user.teamId));
        case 'manager': return lower(userNameById.get(user.managerId));
        case 'fte': return user.fteFraction ?? 1;
        case 'capacity': return user.capacityFactor ?? 1;
        case 'role': return user.role || 'member';
        case 'active': return user.isActive ? 1 : 0;
        default: return lower(user[key]);
      }
    }),
    [users, sort, teamNameById, userNameById],
  );

  function dateDraft(user, field) {
    const draft = drafts[user.id]?.[field];
    if (draft === undefined) return undefined;
    return draft || null;
  }

  function patchUser(user) {
    const capacityDraft = drafts[user.id]?.capacityFactor;
    return apiFetch(`/users/${user.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        // `null`, not `undefined` -- JSON.stringify drops undefined keys entirely, which would make
        // selecting "—" a no-op instead of clearing the assignment.
        teamId: fieldValue(user, 'teamId') || null,
        managerId: fieldValue(user, 'managerId') || null,
        fteFraction: fieldValue(user, 'fteFraction') ? Number(fieldValue(user, 'fteFraction')) : undefined,
        capacityFactor: capacityFactorFromPct(capacityDraft),
        // Same "only when edited" rule as capacity, and for the same reason -- but these two DO need
        // to send explicit null, since clearing a date is a real edit. An untouched date stays
        // undefined so it's dropped from the body entirely.
        capacityStartDate: dateDraft(user, 'capacityStartDate'),
        capacityEndDate: dateDraft(user, 'capacityEndDate'),
        role: fieldValue(user, 'role') || undefined,
        email: fieldValue(user, 'email') || undefined,
        isActive: fieldValue(user, 'isActive', true),
      }),
    });
  }

  // One save for the whole grid (Jeremy, 2026-08-03) instead of a button per row. Rows save
  // independently in parallel, so one bad edit doesn't block the rest -- only the failed rows stay
  // dirty afterward, ready to fix and retry.
  async function saveAll() {
    const dirtyIds = Object.keys(drafts);
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    setError('');
    const failedIds = new Set();
    const failureMessages = [];
    await Promise.all(
      dirtyIds.map(async (id) => {
        const user = users.find((u) => u.id === id);
        if (!user) return;
        try {
          await patchUser(user);
        } catch (err) {
          failedIds.add(id);
          failureMessages.push(`${user.name}: ${err.message}`);
        }
      }),
    );
    try {
      // includeViewers -- without it, every viewer row would vanish from this grid right after saving.
      setUsers(await apiFetch('/users?includeViewers=true'));
    } catch (err) {
      failureMessages.push(err.message);
    }
    setDrafts((prev) => {
      // Start from `prev`, not `{}` -- edits made to other rows while this save was in flight land in
      // `prev` and must survive. Only remove the ids this batch actually attempted and succeeded on;
      // failed ids and anything untouched by this batch stay exactly as they are.
      const next = { ...prev };
      for (const id of dirtyIds) if (!failedIds.has(id)) delete next[id];
      return next;
    });
    setError(failureMessages.length > 0 ? `${failureMessages.length} update(s) failed: ${failureMessages.join('; ')}` : '');
    setSavingAll(false);
  }

  const dirtyCount = Object.keys(drafts).length;

  return (
    <div>
      {dirtyCount > 0 && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
          </div>
          <button className="btn btn-primary" disabled={savingAll} onClick={saveAll}>
            {savingAll ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
      <ErrorBanner error={error} />
      {rampFor && (
        <MonthlyCapacityModal
          user={rampFor}
          onClose={() => setRampFor(null)}
          // Reload so the row's month count and any window change are picked up. Unsaved grid drafts
          // are dropped by load(), so only open this with the grid clean -- guarded on the button.
          onChanged={load}
        />
      )}
      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <div className="sheet-wrap is-fit">
          <table className="sheet">
            <thead>
              <tr>
                <SortTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} style={{ minWidth: 160 }} />
                <SortTh label="Email" sortKey="email" sort={sort} onSort={toggleSort} style={{ minWidth: 200 }} />
                <SortTh label="Team" sortKey="team" sort={sort} onSort={toggleSort} style={{ minWidth: 140 }} />
                <SortTh label="Manager" sortKey="manager" sort={sort} onSort={toggleSort} style={{ minWidth: 160 }} />
                <SortTh label="FTE" sortKey="fte" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 90 }} />
                <SortTh
                  label="Capacity" sortKey="capacity" sort={sort} onSort={toggleSort}
                  style={{ textAlign: 'center', width: 100 }}
                  title="Billable capacity -- e.g. 75% for a full-time manager who's only three-quarters billable"
                />
                {/* The employment window capacity counts within (ADR 0043) — blank start means
                    "has always been here", blank end "still here". */}
                <SortTh
                  label="Start" sortKey="capacityStartDate" sort={sort} onSort={toggleSort}
                  style={{ textAlign: 'center', width: 130 }}
                  title="First day employed. Capacity starts the first FULL month on or after this date, so a mid-month start begins the following month."
                />
                <SortTh
                  label="End" sortKey="capacityEndDate" sort={sort} onSort={toggleSort}
                  style={{ textAlign: 'center', width: 130 }}
                  title="Last day employed. Capacity stops after the last full month — leave blank for current staff."
                />
                <th style={{ textAlign: 'center', width: 90 }} title="Months where this person's capacity differs from their usual — an onboarding ramp, leave, or a quarter on an internal project">
                  Ramp
                </th>
                <SortTh label="Role" sortKey="role" sort={sort} onSort={toggleSort} style={{ width: 90 }} />
                <SortTh label="Active" sortKey="active" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 70 }} />
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => {
                const dirty = !!drafts[user.id];
                // 'viewer' (EVP/Finance read-only, 2026-09-17) is not a resourcing seat -- Team/
                // Manager/FTE/Capacity/Ramp are meaningless for one and stay blank on purpose, so
                // nobody accidentally gives a viewer a capacity footprint.
                const isViewer = fieldValue(user, 'role') === 'viewer';
                return (
                  <tr key={user.id} className={dirty ? 'is-dirty' : ''} style={{ opacity: fieldValue(user, 'isActive', true) ? 1 : 0.55 }}>
                    <td style={{ padding: '8px 10px' }}>{user.name}</td>
                    <td style={{ padding: '4px' }}>
                      <input
                        type="email"
                        className="form-input"
                        style={{ border: 'none', background: 'transparent' }}
                        value={fieldValue(user, 'email')}
                        onChange={(e) => setField(user.id, 'email', e.target.value)}
                      />
                    </td>
                    <td style={{ padding: '4px', textAlign: isViewer ? 'center' : undefined, color: isViewer ? 'var(--text-light)' : undefined }}>
                      {isViewer ? '—' : (
                        <select
                          className="form-select"
                          style={{ border: 'none' }}
                          value={fieldValue(user, 'teamId')}
                          onChange={(e) => setField(user.id, 'teamId', e.target.value)}
                        >
                          <option value="">—</option>
                          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ padding: '4px', textAlign: isViewer ? 'center' : undefined, color: isViewer ? 'var(--text-light)' : undefined }}>
                      {isViewer ? '—' : (
                        <select
                          className="form-select"
                          style={{ border: 'none' }}
                          value={fieldValue(user, 'managerId')}
                          onChange={(e) => setField(user.id, 'managerId', e.target.value)}
                        >
                          <option value="">—</option>
                          {/* Viewers excluded here too -- a read-only account should never be
                              selectable as someone else's manager. */}
                          {users.filter((u) => u.id !== user.id && u.role !== 'viewer').map((u) => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="cell-num">
                      {isViewer ? '—' : (
                        <input
                          type="number"
                          min="0"
                          max="1"
                          step="0.1"
                          style={{ width: 60, textAlign: 'center', border: 'none', background: 'transparent', font: 'inherit' }}
                          value={fieldValue(user, 'fteFraction')}
                          onChange={(e) => setField(user.id, 'fteFraction', e.target.value)}
                        />
                      )}
                    </td>
                    <td className="cell-num">
                      {isViewer ? '—' : (
                        <>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="5"
                            title="Billable capacity -- default 100% unless a manager or other role isn't fully billable"
                            style={{ width: 52, textAlign: 'right', border: 'none', background: 'transparent', font: 'inherit' }}
                            value={capacityPct(user)}
                            onChange={(e) => setField(user.id, 'capacityFactor', e.target.value)}
                          />
                          <span style={{ color: 'var(--text-light)' }}>%</span>
                        </>
                      )}
                    </td>
                    <td style={{ padding: '4px', textAlign: isViewer ? 'center' : undefined, color: isViewer ? 'var(--text-light)' : undefined }}>
                      {isViewer ? '—' : (
                        <input
                          type="date"
                          className="form-input"
                          style={{ border: 'none', background: 'transparent', font: 'inherit' }}
                          value={dateValue(user, 'capacityStartDate')}
                          onChange={(e) => setField(user.id, 'capacityStartDate', e.target.value)}
                        />
                      )}
                    </td>
                    <td style={{ padding: '4px', textAlign: isViewer ? 'center' : undefined, color: isViewer ? 'var(--text-light)' : undefined }}>
                      {isViewer ? '—' : (
                        <input
                          type="date"
                          className="form-input"
                          style={{ border: 'none', background: 'transparent', font: 'inherit' }}
                          value={dateValue(user, 'capacityEndDate')}
                          onChange={(e) => setField(user.id, 'capacityEndDate', e.target.value)}
                        />
                      )}
                    </td>
                    <td style={{ textAlign: 'center', padding: '4px' }}>
                      {isViewer ? (
                        <span style={{ color: 'var(--text-light)' }}>—</span>
                      ) : (
                        // Editing overrides reloads the grid, which drops unsaved drafts -- so it's
                        // closed off until the grid is saved rather than quietly losing edits.
                        <button
                          type="button"
                          className="btn btn-outline"
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          disabled={dirtyCount > 0}
                          onClick={() => setRampFor(user)}
                          title={dirtyCount > 0
                            ? 'Save your changes first — editing monthly capacity reloads this grid'
                            : 'Set per-month capacity for this person'}
                        >
                          {rampCounts[user.id] ? `${rampCounts[user.id]} mo` : 'Set'}
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '4px' }}>
                      <select
                        className="form-select"
                        style={{ border: 'none' }}
                        value={fieldValue(user, 'role') || 'member'}
                        onChange={(e) => setField(user.id, 'role', e.target.value)}
                      >
                        {USER_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <td className="cell-check" style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={fieldValue(user, 'isActive', true)}
                        onChange={(e) => setField(user.id, 'isActive', e.target.checked)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ---------- Fiscal Calendar & Company Holidays (both plain date-only config tables) ----------

// Local date key ('YYYY-MM-DD') for a value that came back from the API as a full ISO timestamp.
// Reads UTC components deliberately -- see TimeEntry.js's apiDateKey for why (ADR 0009: the server
// always constructs/serializes date-only values as UTC midnight, so a local-timezone read would
// roll the date back a day west of Greenwich). Shared by the Fiscal Calendar and Company Holidays
// tabs below -- both read/write the same kind of date-only value.
function apiDateKey(value) {
  if (!value) return '';
  const d = new Date(value);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Which periods the two checkboxes leave visible. The rule that isn't obvious: a period that is both
// locked AND archived needs BOTH boxes checked (Jeremy, 2026-08-11) -- unchecking either one hides
// it, so "show locked" alone doesn't drag the whole archived tail back into the grid. Every archived
// period is necessarily locked (the API refuses any other combination), so archived rows are only
// ever reachable with both on.
export function visibleFiscalWeeks(weeks, showLocked, showArchived) {
  return (weeks || []).filter((w) => (showLocked || !w.isLocked) && (showArchived || !w.isArchived));
}

export function FiscalCalendarTab() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateThrough, setGenerateThrough] = useState('');
  const [newWeek, setNewWeek] = useState({ periodName: '', periodMonth: '', startDate: '', endDate: '' });
  const [adding, setAdding] = useState(false);
  const [showLocked, setShowLocked] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [flaggingId, setFlaggingId] = useState('');
  const [sort, toggleSort] = useGridSort();

  // Always fetch the whole calendar and filter in the browser -- it's ~105 rows growing by ~52 a
  // year, and toggling a checkbox shouldn't cost a round trip or blank the grid mid-edit.
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setWeeks(await apiFetch('/fiscal-weeks?includeArchived=true'));
      setDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function fieldValue(week, field) {
    const draft = drafts[week.id];
    if (draft && draft[field] !== undefined) return draft[field];
    if (field === 'startDate' || field === 'endDate') return apiDateKey(week[field]);
    return week[field] ?? '';
  }

  function setField(weekId, field, value) {
    setDrafts((prev) => ({ ...prev, [weekId]: { ...prev[weekId], [field]: value } }));
  }

  function patchWeek(week) {
    return apiFetch(`/fiscal-weeks/${week.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        periodName: fieldValue(week, 'periodName') || undefined,
        periodMonth: fieldValue(week, 'periodMonth') || undefined,
        startDate: fieldValue(week, 'startDate') || undefined,
        // '' means the field was cleared in the grid -- send null to actually clear it server-side,
        // matching UpdateFiscalWeekDto's null-vs-undefined distinction (see the service comment).
        endDate: fieldValue(week, 'endDate') || null,
      }),
    });
  }

  // One save for the whole grid, same convention as Teams/Task Types/People/Projects.
  async function saveAll() {
    const dirtyIds = Object.keys(drafts);
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    setError('');
    const failedIds = new Set();
    const failureMessages = [];
    await Promise.all(
      dirtyIds.map(async (id) => {
        const week = weeks.find((w) => w.id === id);
        if (!week) return;
        try {
          await patchWeek(week);
        } catch (err) {
          failedIds.add(id);
          failureMessages.push(`${week.periodName}: ${err.message}`);
        }
      }),
    );
    try {
      setWeeks(await apiFetch('/fiscal-weeks?includeArchived=true'));
    } catch (err) {
      failureMessages.push(err.message);
    }
    setDrafts((prev) => {
      const next = { ...prev };
      for (const id of dirtyIds) if (!failedIds.has(id)) delete next[id];
      return next;
    });
    setError(failureMessages.length > 0 ? `${failureMessages.length} update(s) failed: ${failureMessages.join('; ')}` : '');
    setSavingAll(false);
  }

  // Lock/archive are toggled one row at a time rather than through the grid's Save changes batch --
  // they're deliberate acts with server-side ordering rules (lock before archive, unarchive before
  // unlock), not field edits, and an immediate reload is what surfaces a rejected one.
  async function toggleFlag(week, field, value) {
    setFlaggingId(week.id);
    setError('');
    try {
      await apiFetch(`/fiscal-weeks/${week.id}`, { method: 'PATCH', body: JSON.stringify({ [field]: value }) });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setFlaggingId('');
    }
  }

  // No FiscalWeek is referenced by foreign key from anywhere else -- reporting queries join by date
  // range, not by id -- so a real delete is safe (unlike Task Types' soft-deactivate, which guards
  // logged hours). Meant for removing a wrong generated row, e.g. the "extend forward" heuristic's
  // occasional stray short trailing week (ADR 0026). Locked rows are refused server-side.
  async function deleteWeek(week) {
    setDeletingId(week.id);
    setError('');
    try {
      await apiFetch(`/fiscal-weeks/${week.id}`, { method: 'DELETE' });
      setWeeks((prev) => prev.filter((w) => w.id !== week.id));
      setDrafts((prev) => {
        if (!(week.id in prev)) return prev;
        const next = { ...prev };
        delete next[week.id];
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId('');
    }
  }

  async function generateMore() {
    setGenerating(true);
    setError('');
    try {
      await apiFetch('/fiscal-weeks/generate', {
        method: 'POST',
        body: JSON.stringify({ through: generateThrough || undefined }),
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  async function addWeek(e) {
    e.preventDefault();
    if (!newWeek.periodName.trim() || !newWeek.periodMonth.trim() || !newWeek.startDate) return;
    setAdding(true);
    setError('');
    try {
      await apiFetch('/fiscal-weeks', {
        method: 'POST',
        body: JSON.stringify({
          periodName: newWeek.periodName.trim(),
          periodMonth: newWeek.periodMonth.trim(),
          startDate: newWeek.startDate,
          endDate: newWeek.endDate || undefined,
        }),
      });
      setNewWeek({ periodName: '', periodMonth: '', startDate: '', endDate: '' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  const dirtyCount = Object.keys(drafts).length;
  // Dates sort on the yyyy-mm-dd key, which orders lexicographically as well as chronologically.
  const visibleWeeks = sortRows(visibleFiscalWeeks(weeks, showLocked, showArchived), sort, (week, key) => {
    switch (key) {
      case 'startDate': case 'endDate': return apiDateKey(week[key]);
      case 'isLocked': return week.isLocked ? 1 : 0;
      case 'isArchived': return week.isArchived ? 1 : 0;
      default: return lower(week[key]);
    }
  });

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 16, maxWidth: 720 }}>
        History (through FY26-W30) was imported from OnePlan's own export and won't match a clean
        formula -- weeks beyond that are generated with a best-effort default (Saturday-Friday,
        fiscal month = calendar month, no automatic split at a month boundary) and may need hand
        correction here before they're relied on for reporting. See ADR 0026.
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 16, maxWidth: 720 }}>
        <strong>Lock</strong> freezes a period's own definition (name, month, dates) so it can't drift
        after reporting has run off it — it does not affect time entry. <strong>Archive</strong> closes
        the period to time entry, and a period has to be locked first.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 20, fontSize: 13, marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showLocked} onChange={(e) => setShowLocked(e.target.checked)} />
          Show locked periods
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived periods
        </label>
        <span style={{ color: 'var(--text-mid)', fontSize: 12 }}>
          Showing {visibleWeeks.length} of {weeks.length}
        </span>
      </div>

      {dirtyCount > 0 && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
          </div>
          <button className="btn btn-primary" disabled={savingAll} onClick={saveAll}>
            {savingAll ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
      <ErrorBanner error={error} />

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <div className="sheet-wrap is-fit" style={{ marginBottom: 16, maxHeight: 480, overflowY: 'auto' }}>
            <table className="sheet">
              <thead>
                <tr>
                  <SortTh label="Period name" sortKey="periodName" sort={sort} onSort={toggleSort} style={{ width: 140 }} />
                  <SortTh label="Period month" sortKey="periodMonth" sort={sort} onSort={toggleSort} style={{ width: 140 }} />
                  <SortTh label="Start date" sortKey="startDate" sort={sort} onSort={toggleSort} style={{ width: 150 }} />
                  <SortTh label="End date" sortKey="endDate" sort={sort} onSort={toggleSort} style={{ width: 150 }} />
                  <SortTh label="Locked" sortKey="isLocked" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 90 }} />
                  <SortTh label="Archived" sortKey="isArchived" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 100 }} />
                  <th style={{ width: 50 }} />
                </tr>
              </thead>
              <tbody>
                {visibleWeeks.map((week) => {
                  const dirty = !!drafts[week.id];
                  const busy = flaggingId === week.id;
                  // Locked rows render read-only rather than letting an edit be typed and then
                  // rejected on save -- the lock is the whole point of the flag.
                  const frozen = !!week.isLocked;
                  const cellStyle = { border: 'none', background: 'transparent', ...(frozen ? { color: 'var(--text-mid)' } : {}) };
                  return (
                    <tr key={week.id} className={dirty ? 'is-dirty' : ''} style={week.isArchived ? { opacity: 0.65 } : undefined}>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={cellStyle}
                          disabled={frozen}
                          value={fieldValue(week, 'periodName')}
                          onChange={(e) => setField(week.id, 'periodName', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={cellStyle}
                          disabled={frozen}
                          value={fieldValue(week, 'periodMonth')}
                          onChange={(e) => setField(week.id, 'periodMonth', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input
                          type="date"
                          className="form-input"
                          style={cellStyle}
                          disabled={frozen}
                          value={fieldValue(week, 'startDate')}
                          onChange={(e) => setField(week.id, 'startDate', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input
                          type="date"
                          className="form-input"
                          style={cellStyle}
                          disabled={frozen}
                          value={fieldValue(week, 'endDate')}
                          onChange={(e) => setField(week.id, 'endDate', e.target.value)}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn btn-outline"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          disabled={busy || (frozen && week.isArchived)}
                          title={frozen && week.isArchived ? 'Unarchive this period before unlocking it' : undefined}
                          onClick={() => toggleFlag(week, 'isLocked', !frozen)}
                        >
                          {frozen ? 'Unlock' : 'Lock'}
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn btn-outline"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          disabled={busy || (!week.isArchived && !frozen)}
                          title={!week.isArchived && !frozen ? 'Lock this period before archiving it' : undefined}
                          onClick={() => toggleFlag(week, 'isArchived', !week.isArchived)}
                        >
                          {week.isArchived ? 'Unarchive' : 'Archive'}
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          title={frozen ? 'Unlock this week before deleting it' : 'Delete this week'}
                          disabled={deletingId === week.id || frozen}
                          onClick={() => deleteWeek(week)}
                          style={{
                            border: 'none', background: 'transparent', cursor: frozen ? 'default' : 'pointer',
                            color: 'var(--text-mid)', fontSize: 14, opacity: frozen ? 0.4 : 1,
                          }}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {visibleWeeks.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                      {weeks.length === 0
                        ? 'No fiscal weeks yet — run the backfill script, or generate some below.'
                        : `All ${weeks.length} fiscal periods are hidden by the filters above.`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title">Generate more weeks</div>
            <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 12 }}>
              Extends forward from the latest week above. Leave blank for the default (3 months past
              the latest week) — a short buffer is safer than a year, since these need review.
            </div>
            <form onSubmit={(e) => { e.preventDefault(); generateMore(); }} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, width: 200 }}>
                <label className="form-label">Through (optional)</label>
                <input
                  type="date"
                  className="form-input"
                  value={generateThrough}
                  onChange={(e) => setGenerateThrough(e.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={generating}>
                {generating ? 'Generating…' : 'Generate'}
              </button>
            </form>
          </div>

          <div className="card">
            <div className="card-title">Add a week</div>
            <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 12 }}>
              For a manual correction the generator wouldn't produce on its own — e.g. splitting one
              week into two at a quarter close, the way FY26-W25A/B was split by hand.
            </div>
            <form onSubmit={addWeek} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, width: 140 }}>
                <label className="form-label">Period name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="FY26-W25B"
                  maxLength={20}
                  value={newWeek.periodName}
                  onChange={(e) => setNewWeek((f) => ({ ...f, periodName: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 140 }}>
                <label className="form-label">Period month</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="FY26-M07"
                  maxLength={20}
                  value={newWeek.periodMonth}
                  onChange={(e) => setNewWeek((f) => ({ ...f, periodMonth: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 160 }}>
                <label className="form-label">Start date</label>
                <input
                  type="date"
                  className="form-input"
                  value={newWeek.startDate}
                  onChange={(e) => setNewWeek((f) => ({ ...f, startDate: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 160 }}>
                <label className="form-label">End date (optional)</label>
                <input
                  type="date"
                  className="form-input"
                  value={newWeek.endDate}
                  onChange={(e) => setNewWeek((f) => ({ ...f, endDate: e.target.value }))}
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={adding || !newWeek.periodName.trim() || !newWeek.periodMonth.trim() || !newWeek.startDate}
              >
                {adding ? 'Adding…' : '+ Add'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Company Holidays ----------

// Company-wide non-working days -- Time Entry grays these out the same as weekends
// (TimeEntry.js's holidayKeys). No lock/archive, no isActive: unlike Fiscal Calendar, nothing else
// references a holiday by foreign key, so a wrong row is just deleted rather than deactivated.
export function HolidaysTab() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '' });
  const [adding, setAdding] = useState(false);
  const [sort, toggleSort] = useGridSort('date');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setHolidays(await apiFetch('/company-holidays'));
      setDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function fieldValue(holiday, field) {
    const draft = drafts[holiday.id];
    if (draft && draft[field] !== undefined) return draft[field];
    if (field === 'date') return apiDateKey(holiday[field]);
    return holiday[field] ?? '';
  }

  function setField(holidayId, field, value) {
    setDrafts((prev) => ({ ...prev, [holidayId]: { ...prev[holidayId], [field]: value } }));
  }

  function patchHoliday(holiday) {
    return apiFetch(`/company-holidays/${holiday.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: fieldValue(holiday, 'name') || undefined,
        date: fieldValue(holiday, 'date') || undefined,
      }),
    });
  }

  // One save for the whole grid, same convention as every other tab on this page.
  async function saveAll() {
    const dirtyIds = Object.keys(drafts);
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    setError('');
    const failedIds = new Set();
    const failureMessages = [];
    await Promise.all(
      dirtyIds.map(async (id) => {
        const holiday = holidays.find((h) => h.id === id);
        if (!holiday) return;
        try {
          await patchHoliday(holiday);
        } catch (err) {
          failedIds.add(id);
          failureMessages.push(`${holiday.name}: ${err.message}`);
        }
      }),
    );
    try {
      setHolidays(await apiFetch('/company-holidays'));
    } catch (err) {
      failureMessages.push(err.message);
    }
    setDrafts((prev) => {
      const next = { ...prev };
      for (const id of dirtyIds) if (!failedIds.has(id)) delete next[id];
      return next;
    });
    setError(failureMessages.length > 0 ? `${failureMessages.length} update(s) failed: ${failureMessages.join('; ')}` : '');
    setSavingAll(false);
  }

  async function deleteHoliday(holiday) {
    setDeletingId(holiday.id);
    setError('');
    try {
      await apiFetch(`/company-holidays/${holiday.id}`, { method: 'DELETE' });
      setHolidays((prev) => prev.filter((h) => h.id !== holiday.id));
      setDrafts((prev) => {
        if (!(holiday.id in prev)) return prev;
        const next = { ...prev };
        delete next[holiday.id];
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId('');
    }
  }

  async function addHoliday(e) {
    e.preventDefault();
    if (!newHoliday.name.trim() || !newHoliday.date) return;
    setAdding(true);
    setError('');
    try {
      await apiFetch('/company-holidays', {
        method: 'POST',
        body: JSON.stringify({ name: newHoliday.name.trim(), date: newHoliday.date }),
      });
      setNewHoliday({ name: '', date: '' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  const dirtyCount = Object.keys(drafts).length;
  const sortedHolidays = sortRows(holidays, sort, (holiday, key) => (key === 'date' ? apiDateKey(holiday.date) : lower(holiday[key])));

  return (
    <div>
      {dirtyCount > 0 && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
          </div>
          <button className="btn btn-primary" disabled={savingAll} onClick={saveAll}>
            {savingAll ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
      <ErrorBanner error={error} />

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <div className="sheet-wrap is-fit" style={{ marginBottom: 16 }}>
            <table className="sheet">
              <thead>
                <tr>
                  <SortTh label="Date" sortKey="date" sort={sort} onSort={toggleSort} style={{ width: 160 }} />
                  <SortTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} style={{ minWidth: 220 }} />
                  <th style={{ width: 50 }} />
                </tr>
              </thead>
              <tbody>
                {sortedHolidays.map((holiday) => {
                  const dirty = !!drafts[holiday.id];
                  return (
                    <tr key={holiday.id} className={dirty ? 'is-dirty' : ''}>
                      <td style={{ padding: '4px' }}>
                        <input
                          type="date"
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(holiday, 'date')}
                          onChange={(e) => setField(holiday.id, 'date', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(holiday, 'name')}
                          onChange={(e) => setField(holiday.id, 'name', e.target.value)}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          title="Delete this holiday"
                          disabled={deletingId === holiday.id}
                          onClick={() => deleteHoliday(holiday)}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-mid)', fontSize: 14 }}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {holidays.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                      No holidays yet — add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="card-title">Add a holiday</div>
            <form onSubmit={addHoliday} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, width: 160 }}>
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday((f) => ({ ...f, date: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 240 }}>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Thanksgiving Day"
                  maxLength={100}
                  value={newHoliday.name}
                  onChange={(e) => setNewHoliday((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={adding || !newHoliday.name.trim() || !newHoliday.date}>
                {adding ? 'Adding…' : '+ Add'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

// Administration.js's tree nav renders each of the five Tab functions above directly (imported by
// name) rather than through a wrapper page component -- it owns the section/sub-tab labels and
// selection itself now, since the tree needs both levels' labels to draw the whole thing at once.
